import { useState, useCallback } from 'react';
import { message } from 'antd';
import { m201Api, FM110, FM111 } from '../../../services/m201Api';
import { Mode } from './useM201Mode';

export function useM201Record() {
  const [record, setRecord] = useState<FM110 | null>(null);
  const [lastApiResponse, setLastApiResponse] = useState<FM110 | null>(null);
  const [formData, setFormData] = useState<Partial<FM110>>({});
  const [details, setDetails] = useState<Partial<FM111>[]>([]);
  const [loading, setLoading] = useState(false);

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await m201Api.get(id);
      setRecord(data);
      setLastApiResponse(data);
      setFormData({ ...data });
      setDetails(data.details || []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FM110, value: FM110[keyof FM110]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FM110): FM110[keyof FM110] => {
    return (formData as Record<string, FM110[keyof FM110]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FM110) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FM110,
        details: details as FM111[],
      };
      if (mode === 'add') {
        const result = await m201Api.create(req);
        message.success(`新增成功: ${result.fm110_01}`);
        onSuccess(result);
      } else {
        const result = await m201Api.update(formData.fm110_01!, req);
        message.success('修改成功');
        onSuccess(result);
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '儲存失敗';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [formData, details]);

  /** Delete current record */
  const handleDelete = useCallback(async (onSuccess: () => void) => {
    if (!record?.fm110_01) return;
    setLoading(true);
    try {
      await m201Api.delete(record.fm110_01);
      message.success('刪除成功');
      onSuccess();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record]);

  /** Revert form data to last loaded record */
  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  /** Reset form for add mode */
  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({ fm110_18: '+', fm110_80: 'N' });
    setDetails([]);
  }, []);

  /** Apply result after save */
  const applyResult = useCallback((result: FM110) => {
    setRecord(result);
    setLastApiResponse(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  /** Add a detail row */
  const addDetailRow = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        fm111_02: prev.length + 1,
        fm111_11: '', fm111_13: '', fm111_14: '', fm111_15: '', fm111_16: '', fm111_17: '',
        fm111_18: 0, fm111_19: '', fm111_191: '', fm111_20: '',
        fm111_211: '', fm111_22: false,
        fm111_23: 0,
      },
    ]);
  }, []);

  /** Delete a detail row by index */
  const deleteDetailRow = useCallback((idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
  }, []);

  return {
    record, lastApiResponse, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  };
}
